import { downloadFileFromBlob } from './../../utils/download-blob';
import { ClientEntity } from './../../models/clients';
import { ReturnTypes, IPerformanceData, IAssetPerformanceData } from './../../models/performance';
import { ICashflowAvailableDataResult } from './../../models/cashflow';
import { ExposureTreeNode } from './../../models/holdings';
import { Subscription } from 'rxjs';
import { PercentagePipe } from 'src/app/utils/percentage.pipe';
import { DecimalPipe, DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ICashflowState } from 'src/app/store/state/cashflow.state';
import { IAppState } from 'src/app/store/state/app.state';
import { getHoldingsState } from 'src/app/store/holdings/selectors/holdings.selectors';
import { IHoldingsState } from 'src/app/store/state/holdings.state';
import { IClientState } from 'src/app/store/state/client.state';
import { selectSelectedClientGroup } from 'src/app/store/client/selectors/client.selectors';
import { IClient } from 'src/app/store/client/reducers/client.reducers';
import { getGeneralSelectionState } from 'src/app/store/general-selection/selectors/general-selection.selectors';
import { IGeneralSelectionState } from 'src/app/store/state/general-selection.state';
import { PerformanceState } from 'src/app/store/state/performance.state';
import { selectPerformanceState } from 'src/app/store/performance/selectors/performance.selectors';

@Injectable({
    providedIn: 'root'
})
export class BaseXLSReportService {

    // Client State
    clientSubscription: Subscription;
    clientState: IClientState;
    clientName = '';
    allClientEntities: ClientEntity[] = [];

    // General Selection State
    generalSelectionSubscription: Subscription;
    selectedDate: Date;
    selectedEntities: ClientEntity[];

    // HoldingState
    holdingsSubscription: Subscription;
    holdingsState: IHoldingsState;
    exposureData: any[];
    assetAllocationData: {
        Actual: number,
        ClassificationKey: string,
        ClassificationName: string,
        Total: number
    }[];
    exposuresCurrencyData: ExposureTreeNode;
    maturityProfileData: ExposureTreeNode;
    holdingsValuationData: any[];

    // Cashflow State
    cashflowSubscription: Subscription;
    cashflowDataLoading = false;
    cashflowDataError = false;
    cashflowDataErrorMessage: string;
    cashflowAvailableResult: ICashflowAvailableDataResult;

    // Performance State
    performanceByAssetData: IAssetPerformanceData[];
    performanceByAssetClassificationData: IAssetPerformanceData[];
    performanceSubscription: Subscription;
    performanceDataLoading = false;
    performanceDataError = false;
    performanceDataErrorMessage: string;
    performanceState: PerformanceState;
    portfolioSnapshotData: any[];
    portfolioBreakdownData: any[];
    portfolioReturnsData: any[];
    openingValue: number;
    closingValue: number;
    selectedReturnType = ReturnTypes.XIRR;
    selectedReturnPeriod: string;
    selectedReturnSpanMonths: number;

    totalValue = 0;
    xlsColumns: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

    constructor(public store: Store<IAppState>, public decimalPipe: DecimalPipe, public percentagePipe: PercentagePipe, public datePipe: DatePipe) {
        this.clientSubscription = this.store.pipe(select(selectSelectedClientGroup)).subscribe((group: IClient) => {
            if (group && group.GroupName !== '') {
                this.clientName = group.GroupName;
            } else if (group && group.Members && group.Members.length > 0) {
                if (group.Members[0].Name && group.Members[0].Name !== '') {
                    this.clientName = group.Members[0].Name;
                }
            }

            if (group.Members !== undefined) {
                if (group.Members.length > 0) {
                    this.allClientEntities = group.Members.map((item) => Object.assign({}, item));
                    this.totalValue = this.allClientEntities.reduce((accumulator, current) => {
                        const value = (current && current.NetAssetValue && current.NetAssetValue.Value) ? current.NetAssetValue.Value : 0;
                        return accumulator + value;
                    }, 0);
                }
            }
        });

        this.generalSelectionSubscription = this.store.pipe(select(getGeneralSelectionState)).subscribe((gss: IGeneralSelectionState) => {
            this.selectedDate = gss.toDate;
            this.selectedEntities = gss.selectedEntities;
        });

        this.holdingsSubscription = this.store.pipe(select(getHoldingsState)).subscribe((hs: IHoldingsState) => {
            this.holdingsState = hs;
            const hExposuresData: any = this.holdingsState.holdingsExposuresData;
            if (hExposuresData.length !== 0 && (hExposuresData.constructor === Array)) {
                this.exposureData = hExposuresData;

                const assetAllocationsFullDataObject = this.exposureData.find((element: any) => {
                    return (element.AssetAllocations && element.AssetAllocations.length > 0);
                });

                const assetAllocationsDataSet = (assetAllocationsFullDataObject !== undefined) ? assetAllocationsFullDataObject.AssetAllocations : [];

                // filter out items with classificationName === null
                this.assetAllocationData = assetAllocationsDataSet.filter((tabItem) => {
                    return tabItem.ClassificationName !== null;
                });

                this.exposuresCurrencyData = this.exposureData.find((element: any) => {
                    return (element.data.caption === 'Currency');
                });

                this.maturityProfileData = this.exposureData.find((element: any) => {
                    return (element.data.caption === 'Maturity Profile');
                });
            }

            if (this.holdingsState.holdingsValuationData && this.holdingsState.holdingsValuationData.length > 0) {
                this.holdingsValuationData = this.holdingsState.holdingsValuationData;
            }
        });

        this.performanceSubscription = this.store.pipe(select(selectPerformanceState)).subscribe((ps: PerformanceState) => {
            this.performanceState = ps;

            this.selectedReturnSpanMonths = this.performanceState.settings.selectedReturnSpanMonths;
            this.selectedReturnPeriod = this.performanceState.settings.selectedReturnPeriod;
            this.selectedReturnType = this.performanceState.settings.selectedReturnType;
            this.performanceDataLoading = this.performanceState.loading;
            this.performanceDataError = this.performanceState.error;
            this.performanceDataErrorMessage = this.performanceState.errorMessage;

            const pData: IPerformanceData = this.performanceState.performanceData;
            if (Object.keys(pData).length !== 0 && pData.constructor === Object) {
                this.portfolioSnapshotData = pData.snapshots;
                this.portfolioBreakdownData = pData.breakdown;
                this.portfolioReturnsData = pData.returns;
                this.openingValue = pData.openingValue;
                this.closingValue = pData.closingValue;
            }

            this.performanceByAssetData = this.performanceState.assetPerformanceData;
            this.performanceByAssetClassificationData = this.performanceState.assetClassificationPerformanceData;
        });
    }

    protected writeSheet(workbook, title) {
        workbook.xlsx.writeBuffer()
            .then(function (result) {
                const blob = new Blob([result], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                downloadFileFromBlob(blob, title);
            });
    }

    protected getCurrency(value: number) {
        if(value == null){
            return 0;
        }
        return Number(value.toFixed(2));
    }

    protected decimalNumber(n: number, decimals: number = 0) {
        if(n == null){
            return 0;
        }
        return Number(n.toFixed(decimals));
    }

    protected percentageNumber(n: number, decimals: number = 0){
        if(n == null){
            return 0;
        }
        return Number(n.toFixed(decimals))
    }


    protected getNumberFormat(options: { includeSeparator?: boolean, decimalPlaces?: number }){
        return this.getBaseNumberFormat(options) + '_)';
    }

    protected getPercentageFormat(options: { includeSeparator?: boolean, decimalPlaces?: number }){
        return this.getBaseNumberFormat(options) + '%_)';
    }


    private getBaseNumberFormat(options: {includeSeparator?: boolean, decimalPlaces?: number}){
        let format = '';
        if (options.includeSeparator === true) {
            format = '#,##';
        }
        return format + '0.' + ('0'.repeat(options.decimalPlaces != undefined ? options.decimalPlaces : 0));
    }
}

